import React from 'react';
import NotificationsIcon from '@material-ui/icons/Notifications';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Badge from '@material-ui/core/Badge';
import MenuItemAnchor from './MenuItemAnchor';
import theme from '../../theme';
import fetchJSON from '../../utils/fetchJSON';
import gettext from '../../utils/django/gettext';

function isHidden(user: User | undefined) {
    return !user || !user.isAuthenticated;
}

interface ToolbarProps extends React.HTMLProps<HTMLAnchorElement> {
    user: User | undefined;
    component?: React.ElementType;
    ref?: React.RefObject<any>;
}

interface Notification {
    isViewed: boolean;
    html: string;
    id: number;
}

interface ToolbarState {
    open: boolean;
    notifications: Notification[];
    isLoading: boolean;
}

class Toolbar extends React.Component<ToolbarProps, ToolbarState> {

    ref: React.RefObject<HTMLDivElement>

    constructor(props: ToolbarProps) {
        super(props);
        this.state = {open: false, notifications: [], isLoading: true};
        this.ref = React.createRef();
    }

    componentDidMount() {
        this.fetchNotifications();
    }

    componentDidUpdate(prevProps: ToolbarProps) {
        if (prevProps?.user?.id !== this.props.user?.id) {
            this.fetchNotifications();
        }
    }

    render() {
        if (isHidden(this.props.user)) return null;

        return (<React.Fragment>
            {this.renderButton()}
            {this.renderMenu()}
        </React.Fragment>);
    }

    renderButton() {
        const Component = this.props.component || 'a';

        return (<Component
            {...this.props}
            href='#'
            onClick={(event: React.SyntheticEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                this.setState({open: true});
                this.markNotificationsViewed();
            }}
            style={{
                ...this.props.style,
                paddingRight: theme.spacing(1)
            }}
        >
            <div ref={this.ref}>
                {this.props.children || this.renderButtonIcon()}
            </div>
        </Component>);
    }

    renderButtonIcon() {
        if (this.state.isLoading) return <NotificationsIcon style={{width: 24, height: 24}} />;

        const {notifications} = this.state;
        const unviewedNotifications = notifications.filter(({isViewed}) => !isViewed);

        if (unviewedNotifications.length) {
            return (<Badge badgeContent={unviewedNotifications.length} color='secondary'>
                <NotificationsIcon style={{width: 24, height: 24}} />
            </Badge>);
        } else {
            return <NotificationsIcon style={{width: 24, height: 24}} />;
        }
    }

    renderMenu() {
        return (<Menu
            anchorEl={this.ref.current}
            open={this.state.open}
            onClose={() => this.setState({open: false})}
        >{this.renderMenuInner()}</Menu>);
    }

    renderMenuInner() {
        if (this.state.isLoading) {
            return <CircularProgress style={{margin: theme.spacing(2)}} />;

        } else if (this.state.notifications.length === 0) {
            return <MenuItem>You have no notifications</MenuItem>;

        } else {
            return this.state.notifications.map(notification => (
                <MenuItem
                    key={notification.id}
                    style={{height: 'auto', maxWidth: theme.spacing(30), whiteSpace: 'normal'}}
                >
                    {<div dangerouslySetInnerHTML={{__html: notification.html}} />}
                </MenuItem>
            ));
        }
    }

    async fetchNotifications() {
        if (isHidden(this.props.user)) return [];
        this.setState({...this.state, isLoading: true});

        try {
            const notifications = await fetchJSON('/social/notifications/popup/');
            this.setState({notifications, isLoading: false});
        } catch (e) {
            console.error(e);
        }
    }

    markNotificationsViewed() {
        const {notifications} = this.state;

        const promise = Promise.all(notifications.map(notification =>
            fetch(`/social/notifications/${notification.id}/markviewed/`,
                {method: 'post', credentials: 'same-origin'})
        ));

        promise.then(() => this.setState({
            notifications: notifications.map(notification =>
                ({...notification, isViewed: true})
            )
        }));

        promise.catch(error => { console.error(error); });
    }
}

function HamburgerMenu(props: ToolbarProps) {
    if (isHidden(props.user)) return null;
    return <Toolbar component={MenuItemAnchor} {...props}>{gettext('Notifications')}</Toolbar>;
}

export default {Toolbar, HamburgerMenu};
